export const PRACTICE_AGENT_MESSAGE_VERSION = 1;

export type PracticeAgentPage =
  | 'home'
  | 'patient-search'
  | 'patient-summary'
  | 'patient-consultation'
  | 'patient-record'
  | 'provider'
  | 'other';

export type PracticeAgentSuppressionMode = 'dismissed' | 'snoozed';
export type PracticeAgentPresentationMode = 'present' | 'refresh' | 'hidden';

export type PracticeAgentVisibilityReason = 'card-ready' | 'no-relevant-card' | 'card-api-failed';

export interface PracticeAgentContext {
  practiceNumber: string;
  doctorId: string;
  patientId: string | null;
  currentPage: PracticeAgentPage;
}

export interface PracticeAgentSuppression {
  suppressed: boolean;
  mode?: PracticeAgentSuppressionMode;
  until?: string;
}

export interface PracticeAgentContextPayload extends PracticeAgentContext {
  contextId: number;
  presentationMode: PracticeAgentPresentationMode;
  suppression: PracticeAgentSuppression;
}

export interface HbcAuthChangedMessage {
  type: 'hbc.auth.changed';
  version: 1;
  payload: {
    firebaseIdToken: string;
  };
}

export interface HbcInitMessage {
  type: 'hbc.init';
  version: 1;
  payload: PracticeAgentContextPayload;
}

export interface HbcContextChangedMessage {
  type: 'hbc.context.changed';
  version: 1;
  payload: PracticeAgentContextPayload;
}

export interface HbcSuppressionChangedMessage {
  type: 'hbc.suppression.changed';
  version: 1;
  payload: PracticeAgentSuppression;
}

export type HbcPracticeAgentMessage =
  | HbcAuthChangedMessage
  | HbcInitMessage
  | HbcContextChangedMessage
  | HbcSuppressionChangedMessage;

export interface PaVisibilityChangedMessage {
  type: 'pa.visibility.changed';
  version: 1;
  payload: {
    contextId: number;
    visible: boolean;
    reason: PracticeAgentVisibilityReason;
    cardId?: string;
  };
}

export interface PaSizeChangedMessage {
  type: 'pa.size.changed';
  version: 1;
  payload: {
    contextId: number;
    expanded: boolean;
  };
}

function isObject(value: any): value is { [key: string]: any } {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function hasOnlyKeys(value: { [key: string]: any }, allowedKeys: string[]): boolean {
  return Object.keys(value).every(key => allowedKeys.includes(key));
}

export function isPaVisibilityChangedMessage(value: any): value is PaVisibilityChangedMessage {
  if (!isObject(value) || !hasOnlyKeys(value, ['type', 'version', 'payload'])) {
    return false;
  }

  if (value.type !== 'pa.visibility.changed' || value.version !== PRACTICE_AGENT_MESSAGE_VERSION || !isObject(value.payload)) {
    return false;
  }

  const payload = value.payload;
  if (!hasOnlyKeys(payload, ['contextId', 'visible', 'reason', 'cardId'])) {
    return false;
  }

  if (!Number.isInteger(payload.contextId) || payload.contextId <= 0 || typeof payload.visible !== 'boolean') {
    return false;
  }

  const validReason = ['card-ready', 'no-relevant-card', 'card-api-failed'].includes(payload.reason);
  if (!validReason) {
    return false;
  }

  if (payload.visible && payload.reason !== 'card-ready') {
    return false;
  }

  if (!payload.visible && payload.reason === 'card-ready') {
    return false;
  }

  return payload.cardId === undefined || (typeof payload.cardId === 'string' && payload.cardId.trim().length > 0);
}

export function isPaSizeChangedMessage(value: any): value is PaSizeChangedMessage {
  if (!isObject(value) || !hasOnlyKeys(value, ['type', 'version', 'payload'])) {
    return false;
  }

  if (value.type !== 'pa.size.changed' || value.version !== PRACTICE_AGENT_MESSAGE_VERSION || !isObject(value.payload)) {
    return false;
  }

  const payload = value.payload;
  if (!hasOnlyKeys(payload, ['contextId', 'expanded'])) {
    return false;
  }

  return Number.isInteger(payload.contextId) && payload.contextId > 0 && typeof payload.expanded === 'boolean';
}
